import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BriefcaseBusiness, Loader2, Plus, UserRound, Clock3, BarChart3, AlertCircle } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { useAuxiliarVendas } from '@/hooks/useAuxiliarVendas';
import { VENDA_ETAPAS, VendaEtapa, VendaSessao } from '@/lib/vendaAssistant';
import { AuxiliarVendaCustosAdmin } from '@/components/admin/AuxiliarVendaCustosAdmin';

type FormState = {
  cliente: string;
  oportunidade: string;
  observacoes: string;
  openModal: boolean;
  activeTab: 'kanban' | 'relatorios';
};

const initialFormState: FormState = {
  cliente: '',
  oportunidade: '',
  observacoes: '',
  openModal: false,
  activeTab: 'kanban',
};

export default function AuxiliarVendas() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { state, carregarEmpresaEUsuarios, carregarListaSessoes, criarSessao, moverEtapa } = useAuxiliarVendas();

  const [formState, setFormState] = useState<FormState>(initialFormState);

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      if (!user) {
        navigate('/auth');
        return;
      }
      const empId = await carregarEmpresaEUsuarios(user.id);
      if (empId) {
        await carregarListaSessoes(empId);
      }
    };
    init();
  }, [authLoading, user, carregarEmpresaEUsuarios, carregarListaSessoes, navigate]);

  const handleCreateSession = async () => {
    if (!user || !formState.cliente.trim()) return;

    const novaSessao = await criarSessao(
      {
        cliente: formState.cliente,
        oportunidade: formState.oportunidade,
        observacoes: formState.observacoes,
      },
      user.id
    );

    if (novaSessao) {
      toast({
        title: 'Sessão criada com sucesso!',
        description: `Conversa iniciada para o cliente ${novaSessao.cliente_nome}.`,
      });
      setFormState(initialFormState);
      navigate(`/auxiliar-vendas/sessao/${novaSessao.id}`);
    } else if (state.error) {
      toast({
        title: 'Erro ao criar sessão',
        description: state.error,
        variant: 'destructive',
      });
    }
  };

  const handleMoveStage = async (sessao: VendaSessao, novaEtapa: VendaEtapa) => {
    if (sessao.etapa === novaEtapa) return;
    const ok = await moverEtapa(sessao.id, novaEtapa);
    if (ok) {
      const etapaLabel = VENDA_ETAPAS.find((e) => e.value === novaEtapa)?.label;
      toast({
        title: 'Etapa atualizada',
        description: `${sessao.cliente_nome} movido para ${etapaLabel}.`,
      });
    } else if (state.error) {
      toast({
        title: 'Não foi possível mover a sessão',
        description: state.error,
        variant: 'destructive',
      });
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Layout backTo="/dashboard">
      <div className="max-w-[1700px] mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Auxiliar de Venda em Tempo Real</h1>
            <p className="text-muted-foreground mt-1">
              Acompanhe conversas de vendas, receba diagnósticos instantâneos e movimente o pipeline.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() =>
                setFormState((prev) => ({
                  ...prev,
                  openModal: true,
                }))
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              Nova sessão de venda
            </Button>
          </div>
        </div>

        <Tabs
          value={formState.activeTab}
          onValueChange={(val) =>
            setFormState((prev) => ({
              ...prev,
              activeTab: val as 'kanban' | 'relatorios',
            }))
          }
          className="space-y-6"
        >
          <TabsList>
            <TabsTrigger value="kanban" className="flex items-center gap-2">
              <BriefcaseBusiness className="h-4 w-4" />
              Quadro Kanban
            </TabsTrigger>
            <TabsTrigger value="relatorios" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Uso & Custos de IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kanban" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-start">
              {VENDA_ETAPAS.map((coluna) => {
                const sessoesEtapa = state.sessoes.filter((s) => s.etapa === coluna.value);
                return (
                  <section
                    key={coluna.value}
                    className="rounded-lg border bg-card/60 backdrop-blur-sm shadow-sm min-h-[500px] flex flex-col"
                  >
                    <header className="p-3 border-b flex items-center justify-between bg-muted/40 rounded-t-lg">
                      <div className="min-w-0">
                        <h2 className="font-semibold text-sm truncate">{coluna.label}</h2>
                        <p className="text-[10px] text-muted-foreground truncate">{coluna.description}</p>
                      </div>
                      <Badge variant="secondary" className="ml-2 shrink-0">
                        {sessoesEtapa.length}
                      </Badge>
                    </header>

                    <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                      {sessoesEtapa.map((sessao) => {
                        const responsavelNome = state.usuariosMap[sessao.responsavel_id]?.nome || 'Vendedor';
                        return (
                          <Card
                            key={sessao.id}
                            className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-md border-border/80"
                            onClick={() => navigate(`/auxiliar-vendas/sessao/${sessao.id}`)}
                          >
                            <CardHeader className="p-3 pb-2">
                              <CardTitle className="text-sm font-semibold flex items-start justify-between gap-2">
                                <span className="flex items-center gap-1.5 truncate">
                                  <UserRound className="h-4 w-4 text-primary shrink-0" />
                                  <span className="truncate">{sessao.cliente_nome}</span>
                                </span>
                                <Badge variant="outline" className="text-[10px] uppercase font-mono shrink-0">
                                  {sessao.status}
                                </Badge>
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 pt-0 space-y-3">
                              {sessao.oportunidade && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {sessao.oportunidade}
                                </p>
                              )}
                              <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1 border-t">
                                <span className="flex items-center gap-1 truncate" title={`Responsável: ${responsavelNome}`}>
                                  Resp: {responsavelNome}
                                </span>
                                <span className="flex items-center gap-1 shrink-0">
                                  <Clock3 className="h-3 w-3" />
                                  {new Date(sessao.ultima_interacao_em).toLocaleDateString('pt-BR')}
                                </span>
                              </div>

                              <div className="pt-1">
                                <select
                                  aria-label="Mover sessão de etapa"
                                  className="w-full h-8 rounded-md border bg-background px-2 text-xs text-foreground cursor-pointer hover:border-primary focus:outline-none"
                                  value={sessao.etapa}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => handleMoveStage(sessao, e.target.value as VendaEtapa)}
                                >
                                  {VENDA_ETAPAS.map((stage) => (
                                    <option key={stage.value} value={stage.value}>
                                      Mover para: {stage.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}

                      {!sessoesEtapa.length && (
                        <div className="py-12 text-center text-xs text-muted-foreground space-y-1">
                          <BriefcaseBusiness className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
                          <p>Sem sessões nesta etapa</p>
                        </div>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="relatorios">
            {state.empresaId ? (
              <AuxiliarVendaCustosAdmin empresaId={state.empresaId} />
            ) : (
              <div className="p-8 text-center text-muted-foreground flex items-center justify-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Selecione uma empresa para visualizar o relatório de consumo.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Criação de Sessão */}
      <Dialog
        open={formState.openModal}
        onOpenChange={(open) =>
          setFormState((prev) => ({
            ...prev,
            openModal: open,
          }))
        }
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Sessão de Venda</DialogTitle>
            <DialogDescription>
              Informe o cliente e o contexto inicial. O Auxiliar de Venda acompanhará a conversa ao vivo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="cliente">Nome do Cliente *</Label>
              <Input
                id="cliente"
                placeholder="Ex: João da Silva / Empresa X"
                maxLength={160}
                value={formState.cliente}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    cliente: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <Label htmlFor="oportunidade">Empresa ou Oportunidade</Label>
              <Input
                id="oportunidade"
                placeholder="Ex: Licença Software ERP - R$ 50k"
                maxLength={200}
                value={formState.oportunidade}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    oportunidade: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <Label htmlFor="observacoes">Observações Iniciais</Label>
              <Textarea
                id="observacoes"
                placeholder="Ex: Cliente interessado em redução de custos, precisa de resposta rápida."
                maxLength={3000}
                rows={4}
                value={formState.observacoes}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    observacoes: e.target.value,
                  }))
                }
              />
            </div>

            <Button
              className="w-full"
              disabled={state.saving || !formState.cliente.trim()}
              onClick={handleCreateSession}
            >
              {state.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar e Iniciar Sessão
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}